<script lang="ts" setup>
import { computed } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { useRouteParams } from '@vueuse/router';
import { projectDetailQuery } from '@/graphql/queries/project';
import { useUserSettingsStore } from '@/stores/user-settings';
import { VContainer, VRow, VCol } from 'vuetify/components';
import ProjectDescriptionPortfolioCard from '@/components/ProjectDescriptionPortfolioCard.vue';
import ProjectDescriptionTeamCard from './ProjectDescriptionTeamCard.vue';
import ProjectDescriptionPeriodCard from './ProjectDescriptionPeriodCard.vue';
import ProjectDescriptionMainCard from './ProjectDescriptionMainCard.vue';

const projectId = useRouteParams<string>('id');
const userSettings = useUserSettingsStore();

const projectDetailVariables = computed(() => ({
    projectId: projectId.value,
    portfolioId: userSettings.activePortfolio?.id,
}));

// Single
const { result: projectDetailResult, loading: loadingDetail } = useQuery(
    projectDetailQuery,
    projectDetailVariables
);

const projectDetail = computed(
    () => projectDetailResult.value?.project || undefined
);

function concatName(userEntity: { firstName: string; lastName: string }) {
    return userEntity.firstName + ' ' + userEntity.lastName;
}

const portfolioCardInfo = computed(() => ({
    sourcePortfolio: projectDetail.value?.masterPortfolio.name ?? '',
    theme: projectDetail.value?.themes?.[0]?.name,
    program: projectDetail.value?.programs?.[0]?.name,
    department: projectDetail.value?.departments?.[0]?.name,
}));

const teamCardInfo = computed(() => ({
    programManager: projectDetail.value?.programManager
        ? concatName(projectDetail.value.programManager)
        : undefined,
    projectManager: projectDetail.value?.projectManager
        ? concatName(projectDetail.value.projectManager)
        : undefined,
    team: parseTeamMembers(projectDetail.value?.projectTeam),
}));

const periodCardInfo = computed(() => ({
    startDate: projectDetail?.value?.startDate,
    endDate: projectDetail?.value?.endDate,
}));

const projectDescription = computed(() => ({
    description: projectDetail?.value?.description,
    goal: projectDetail?.value?.goal,
    scope: projectDetail?.value?.scope,
    result: projectDetail?.value?.result,
    risks: projectDetail?.value?.risks,
    stakeholders: projectDetail?.value?.stakeholders,
    onHold: Boolean(projectDetail?.value?.onHold),
    onHoldReason: projectDetail?.value?.onHoldReason,
}));

function parseTeamMembers(teamMembers: string) {
    try {
        const y = JSON.parse(teamMembers);

        if (typeof y !== 'object' && !Array.isArray(y)) {
            return [];
        }

        return y
            .filter(
                (teamMember: {
                    firstName?: string;
                    lastName?: string;
                }) => teamMember.firstName || teamMember.lastName
            )
            .map((teamMember: { firstName: string; lastName: string }) =>
                concatName(teamMember)
            );
    } catch {
        return [];
    }
}
</script>

<template>
    <v-container class="pt-6">
        <v-row>
            <v-col md="4" class="d-flex flex-column ga-6">
                <ProjectDescriptionPortfolioCard
                    :source-portfolio="portfolioCardInfo.sourcePortfolio"
                    :theme="portfolioCardInfo.theme"
                    :program="portfolioCardInfo.program"
                    :department="portfolioCardInfo.department"
                    :loading="loadingDetail"
                />

                <ProjectDescriptionTeamCard
                    data-name="team-card"
                    :program-manager="teamCardInfo.programManager"
                    :project-manager="teamCardInfo.projectManager"
                    :team-members="teamCardInfo.team"
                    :loading="loadingDetail"
                />

                <ProjectDescriptionPeriodCard
                    data-name="period-card"
                    :start-date="periodCardInfo.startDate"
                    :end-date="periodCardInfo.endDate"
                    :loading="loadingDetail"
                />
            </v-col>
            <v-col md="8">
                <ProjectDescriptionMainCard
                    v-if="!loadingDetail"
                    data-name="main-card"
                    :description="projectDescription.description"
                    :goal="projectDescription.goal"
                    :scope="projectDescription.scope"
                    :result="projectDescription.result"
                    :risks="projectDescription.risks"
                    :stakeholders="projectDescription.stakeholders"
                    :on-hold="projectDescription.onHold"
                    :on-hold-reason="projectDescription.onHoldReason"
                />
            </v-col>
        </v-row>
    </v-container>
</template>
